import React, { Dispatch, memo, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SortType } from "rsuite-table";
import { extend, isString, isUndefined, pick, pickBy } from "lodash";
import Color from "color";
import { useRefSize } from "uikit";

import { Order, Language } from "../../../../../services";
import { useTypedSelector } from "../../../../../redux/store";
import { ORDERS_TYPES } from "../../../../../redux/constants/OrdersPage";
import { useKeyBind, useOrderSubHandler } from "../../../../../hooks";
import { makeLookupTable } from "../../../../../utils";
import {
	getOrderPaymentStatus,
	paymentStatusColorKey,
	ROW_TINT_PAYMENT_STATUSES,
} from "../../../../../utils/orderPaymentStatus";
import { ROUTES } from "../../../../../constants/routes";
import { Colors, StyledGrid } from "../../../../common";
import { CompactTable, TableSettings } from "../../../..";
import { orderTableByType } from "../..";
import PerfProfiler from "../../../../../tests/PerfProfiler";

import TableRoot from "./components/TableRoot";
import columnBase, {
	ColumnId,
	ColumnId as LocalColumnId,
	defaultColumnIds,
} from "./columns";
import prepareOrderTableData from "./prepareOrderTableData";

const TABLE_HEADER_HEIGHT = 26;
const TABLE_ROW_HEIGHT = 32;
/** Opacity of the whole-row tint while the payment is awaited. */
const PAYMENT_ROW_TINT_ALPHA = 0.3;

const ModelTableBase: React.FC<ModelTable.Props> = memo(
	({
		selected,
		sort,
		columns,

		loading,
		data,
		language,

		onChangeSelected,
		onChangeSort,
		onChangeColumns,

		onEdit,
		onLoadMore,
		totalCount,
	}) => {
		const { pathname } = useLocation();
		const { t } = useTranslation();

		const { getSortAndLimitOrderSubHandler } = useOrderSubHandler();

		const ordersType = useTypedSelector(
			(state) => state.ordersPageReducer.ordersType,
		);
		const hideOrderIds = useTypedSelector(
			(state) => state.ordersPageReducer.hideOrderIds,
		);
		const tableType = useMemo(
			() => orderTableByType[ordersType],
			[ordersType],
		);

		const items = useMemo(() => {
			const models = Array.isArray(data) ? data : [];
			const defaultColumn =
				ordersType === ORDERS_TYPES.CLOSED
					? ColumnId.CreateOrderDate
					: ColumnId.ExecutorArrivalTime;
			const defaultType =
				ordersType === ORDERS_TYPES.CLOSED ? "desc" : "asc";
			const [column, type] = [
				sort?.column || defaultColumn,
				sort?.type || defaultType,
			];
			const options: Order.SubscribeOptions = {
				order:
					typeof column === "string" && typeof type === "string"
						? ({
								[column]: type,
						  } as Order.SubscribeOptions["order"])
						: ({} as Order.SubscribeOptions["order"]),
			};

			const modelsSort = getSortAndLimitOrderSubHandler(options, models);

			return prepareOrderTableData(modelsSort, hideOrderIds);
		}, [
			data,
			getSortAndLimitOrderSubHandler,
			hideOrderIds,
			ordersType,
			sort?.column,
			sort?.type,
		]);

		const tableSettingColumns = useMemo(
			() =>
				defaultColumnIds.map((defaultColumnId) => ({
					id: defaultColumnId,
					label: t([
						columnBase[defaultColumnId]?.pathTranslation,
						columnBase[defaultColumnId]?.label,
						"",
					]),
				})),
			[t],
		);

		const canLoadMoreRef = useRef(true);
		const tableRef = useRef<CompactTable.Ref | null>(null);

		const { ref: tableRootRef, size: tableRootSize } = useRefSize();

		const colors = useTypedSelector((state) => state.settings.colors);

		const columnLookup = useMemo(
			() => makeLookupTable(columns, (column) => column.id, true),
			[columns],
		);

		const tableRootColors = useMemo(() => {
			const defaultOrderColors = pickBy(colors, (_, colorName) =>
				colorName.startsWith("color"),
			);

			const paymentTypeOrderColors = pick(colors, [
				"app",
				"phone",
				"website",
				"executor",
				"dispatcher",
				"orderFromTransfer",
				"orderAcceptedFromTransfer",
				"orderToTransfer",
				"orderAcceptedToTransfer",
			]);

			const orderColors = {
				...defaultOrderColors,
				...paymentTypeOrderColors,
			};

			const selectedOrderColors = Object.fromEntries(
				Object.entries(orderColors).map(([colorName, colorValue]) => {
					const color = Color(colorValue as string);

					return [
						`selected-${colorName}`,
						color.white() === 100
							? colors.activeOrder
							: color.darken(0.1).hex(),
					];
				}),
			);

			// Whole-row tint while the payment is awaited: the status color,
			// semi-transparent (hexa keeps the alpha the selected variant needs).
			const paymentTintColors = Object.fromEntries(
				ROW_TINT_PAYMENT_STATUSES.flatMap((status) => {
					const colorName = paymentStatusColorKey(status);
					const color = Color(colors[colorName]).alpha(
						PAYMENT_ROW_TINT_ALPHA,
					);
					return [
						[colorName, color.hexa()],
						[`selected-${colorName}`, color.darken(0.1).hexa()],
					];
				}),
			);

			return {
				selected: colors.activeOrder,
				...orderColors,
				...selectedOrderColors,
				...paymentTintColors,
			};
		}, [colors]);

		const tableRowClassName = useCallback(
			(item?: Order.Model) => {
				if (isUndefined(item)) return "";

				if ((item as { __isPadding?: boolean }).__isPadding) {
					return "padding-row";
				}

				const orderColor =
					item.additionalFields?.displaySettings?.color;

				const classNameNodes: string[] = [];

				if (selected === item.id) {
					classNameNodes.push("selected");
				}
				const paymentStatus = getOrderPaymentStatus(item);

				if (isString(orderColor) && orderColor !== "none") {
					classNameNodes.push(orderColor as string);
				} else if (
					(!isUndefined(item.isOwn) &&
						item?.additionalFields?.transfer) ||
					(!item.isOwn &&
						item?.additionalFields?.transfer &&
						item?.transfer)
				) {
					if (
						paymentStatus &&
						ROW_TINT_PAYMENT_STATUSES.includes(paymentStatus)
					) {
						classNameNodes.push(
							paymentStatusColorKey(paymentStatus),
						);
					}
					const {
						status,
						isOwn,
						additionalFields,
						executorToOrder,
						taxiService,
						globalId,
					} = item;
					const { transfer } = additionalFields;

					const executor =
						executorToOrder && executorToOrder?.length
							? executorToOrder?.[0]?.executor
							: undefined;

					if (
						isOwn &&
						executor?.taxiService?.id !== taxiService?.id
					) {
						if (
							transfer?.sendProcess?.currentTransferStage &&
							!executorToOrder?.length &&
							!transfer?.lastReceivedOrder?.executorToOrder
								?.length
						) {
							classNameNodes.push("orderToTransfer"); // orange
						}

						if (
							transfer?.lastReceivedOrder?.executorToOrder?.length
						) {
							classNameNodes.push("orderAcceptedFromTransfer"); // pink
						}
					}

					if (!isOwn) {
						if (!executorToOrder?.length) {
							if (
								status === "closed" &&
								transfer?.executor !== null &&
								globalId
							) {
								classNameNodes.push("orderAcceptedToTransfer"); // green
							} else {
								classNameNodes.push("orderFromTransfer"); // blue
							}
						}

						if (executorToOrder?.length) {
							classNameNodes.push("orderAcceptedToTransfer"); // green
						}
					}
				} else if (
					paymentStatus &&
					ROW_TINT_PAYMENT_STATUSES.includes(paymentStatus)
				) {
					classNameNodes.push(paymentStatusColorKey(paymentStatus));
				} else {
					classNameNodes.push(item.source);
				}

				return classNameNodes.join("-");
			},
			[selected],
		);

		const onLoadMoreRef = useRef(onLoadMore);

		onLoadMoreRef.current = onLoadMore;

		const loadedCount = items.length;

		const tableData = useMemo(() => {
			const total = Math.max(totalCount ?? loadedCount, loadedCount);
			if (total <= loadedCount) return items;

			const placeholders = Array.from(
				{ length: total - loadedCount },
				(_, index) =>
					({
						id: `padding-bottom-${loadedCount + index}`,
						__isPadding: true,
					} as unknown as Order.Model),
			);

			return [...items, ...placeholders];
		}, [items, loadedCount, totalCount]);

		const tableOnScroll = useMemo(() => {
			canLoadMoreRef.current = true;

			return (_x: number, y: number) => {
				if (!canLoadMoreRef.current || loadedCount === 0) return;

				const loadedHeight = loadedCount * TABLE_ROW_HEIGHT;
				const top = Math.abs(y);
				const tableContainerHeight =
					tableRef.current?.root.getBoundingClientRect().height ?? 0;

				if (
					loadedHeight - top - tableContainerHeight <
					TABLE_ROW_HEIGHT * 8
				) {
					onLoadMoreRef.current();
					canLoadMoreRef.current = false;
				}
			};
		}, [loadedCount]);

		const tableOnRowClick = useCallback(
			(rowData: Order.Model) => {
				if ((rowData as { __isPadding?: boolean }).__isPadding) return;
				onChangeSelected(rowData.id);
			},
			[onChangeSelected],
		);

		const tableOnRowDoubleClick = useCallback(
			(rowData: Order.Model) => {
				if ((rowData as { __isPadding?: boolean }).__isPadding) return;
				onEdit(rowData.id);
			},
			[onEdit],
		);

		const tableOnChangeSort = useCallback(
			(column: string, type: SortType = "asc") =>
				onChangeSort({ column: column as LocalColumnId, type }),
			[onChangeSort],
		);

		const tableSettingsValue = useMemo(
			() =>
				columns
					.filter((column) => column.visible)
					.map((column) => column.id),
			[columns],
		);

		const tableSettingsOnChange = useCallback(
			(columnIds: string[]) => {
				const otherColumns = columns
					.filter((column) => !columnIds.includes(column.id))
					.map((column) => ({ ...column, visible: false }));

				const newColumns = columnIds.map((columnId) => ({
					id: columnId as LocalColumnId,
					width: columnLookup[columnId]?.value.width,
					visible: true,
				}));

				const a = [...newColumns, ...otherColumns];

				onChangeColumns(a);
			},
			[columnLookup, columns, onChangeColumns],
		);

		const renderedColumns = useMemo(
			() =>
				columns
					.filter((column) => column.visible)
					.map((column) => {
						const node = columnBase[column.id].render({
							width: column.width || 100,
							language,
							tableType,

							onResize: (width, columnId) => {
								const newColumns = columns.map((columns) =>
									columns.id === columnId
										? { ...columns, width: width ?? 0 }
										: columns,
								);

								onChangeColumns(newColumns);
							},
						});

						return node;
					}),
			[columns, language, onChangeColumns, tableType],
		);

		const moveSelectedBy = useCallback(
			(offset: number) => {
				const selectedIndex = items.findIndex(
					(order) => order.id === selected,
				);

				if (selectedIndex === -1) return;

				const nextSelectedIndex = Math.max(
					Math.min(selectedIndex + offset, items.length - 1),
					0,
				);

				onChangeSelected(items[nextSelectedIndex].id);
			},
			[items, onChangeSelected, selected],
		);

		useKeyBind(["ArrowUp"], (event) => {
			event.preventDefault();
			event.stopPropagation();

			moveSelectedBy(-1);
		});

		useKeyBind(["ArrowDown"], (event) => {
			event.preventDefault();
			event.stopPropagation();

			moveSelectedBy(1);
		});

		return (
			<PerfProfiler id="OrderTable">
				<StyledGrid
					over="hidden"
					areas=""
					rows="1fr auto"
					w="100%"
					h="100%"
					bgC={
						pathname === ROUTES.ORDERS
							? Colors.BACKGROUND_BLUE_1
							: Colors.BACKGROUND_WHITE
					}
				>
					<TableRoot ref={tableRootRef} colors={tableRootColors}>
						<CompactTable
							ref={tableRef}
							fillHeight
							virtualized
							height={tableRootSize.height}
							disableDoubleClickDebounce
							shouldUpdateScroll={false}
							headerHeight={TABLE_HEADER_HEIGHT}
							rowHeight={TABLE_ROW_HEIGHT}
							sortColumn={sort?.column}
							sortType={sort?.type}
							loading={loading}
							data={tableData}
							rowClassName={tableRowClassName}
							onScroll={tableOnScroll}
							onRowClick={tableOnRowClick}
							onRowDoubleClick={tableOnRowDoubleClick}
							onSortColumn={tableOnChangeSort}
						>
							{renderedColumns}
						</CompactTable>
					</TableRoot>

					{pathname !== ROUTES.ORDERS && (
						<TableSettings
							value={tableSettingsValue}
							defaultValue={defaultColumnIds}
							columns={tableSettingColumns}
							onChange={tableSettingsOnChange}
						/>
					)}
				</StyledGrid>
			</PerfProfiler>
		);
	},
);

const ModelTable = extend(ModelTableBase, {
	ColumnId: LocalColumnId,
});

declare namespace ModelTable {
	type ColumnId = LocalColumnId;

	interface Sort {
		column?: LocalColumnId;
		type?: SortType;
	}

	interface Column {
		id: LocalColumnId;
		width: number;
		visible: boolean;
	}

	interface Props {
		selected?: number;
		sort?: Sort;

		columns: Column[];
		loading?: boolean;
		data: Order.Model[];
		language: Language;

		onChangeSelected: Dispatch<number>;
		onChangeSort: Dispatch<Sort>;
		onChangeColumns: Dispatch<Column[]>;

		onEdit: Dispatch<number>;
		onLoadMore: () => void;
		/** Real total rows for scrollbar height; unloaded slots are padded. */
		totalCount?: number;
	}
}

export default ModelTable;
